import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { loadRazorpayScript } from "@/lib/razorpay";
import { dispatchCheckoutOpen, dispatchCheckoutClose } from "@/lib/checkoutOverlay";
import { trackInitiateCheckout, trackPurchase, getFbCookies } from "@/lib/analytics";
import { getAttribPayload } from "@/lib/attribution";
import { getSrcTrackingPayload } from "@/lib/srcTracking";
import { useToast } from "@/hooks/use-toast";
import { MonthlyTierPicker } from "@/components/donation/MonthlyTierPicker";
import { DiwaliTierPicker } from "@/components/donation/DiwaliTierPicker";

const AMTM_LOGO_URL = "https://customer-assets-m6fa6gv7.emergentagent.net/job_amtm-animal-care/artifacts/6ng7d8bg_amtm_logo_trim.png";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const MIN_DONATION = 100;
const DIWALI_CAMPAIGN_TAG = "diwali2026";

/**
 * Diwali gift widget - a clone of GuardianDonationWidget for the /diwali
 * page and homepage Diwali hero. Same checkout, PAN, residency, receipt and
 * webhook flow as the Guardian widget; only the copy, default toggle and
 * one-time tier list are Diwali-specific. Monthly still creates a normal
 * Guardian subscription, just tagged campaign=diwali2026.
 */
export default function DiwaliDonationWidget({ onBeforeCheckout, onCheckoutCancelled, className = "", testIdPrefix = "diwali" }) {
  const tid = (suffix) => `${testIdPrefix}-${suffix}`;
  const [donationType, setDonationType] = useState("one_time");
  const [monthlyAmount, setMonthlyAmount] = useState(1000);
  const [oneTimeAmount, setOneTimeAmount] = useState(1000);
  const [customAmount, setCustomAmount] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [pan, setPan] = useState("");
  const [residency, setResidency] = useState(null); // null | "yes" | "no"
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const { toast } = useToast();

  const selectedAmount = donationType === "monthly" ? monthlyAmount : oneTimeAmount;
  const effectiveAmount = customAmount ? parseFloat(customAmount) || 0 : selectedAmount;
  const customAmountBelowMin = customAmount !== "" && (parseFloat(customAmount) || 0) < MIN_DONATION;

  const switchType = (type) => {
    setDonationType(type);
    setCustomAmount("");
  };

  const handleDonate = async () => {
    if (effectiveAmount < MIN_DONATION) {
      toast({ variant: "destructive", title: `Minimum donation is ₹${MIN_DONATION}` });
      return;
    }
    if (!name.trim()) {
      toast({ variant: "destructive", title: "Full name is required" });
      return;
    }
    if (!EMAIL_REGEX.test(email.trim())) {
      toast({ variant: "destructive", title: "Enter a valid email address" });
      return;
    }
    if (!phone.trim() || phone.trim().length < 10) {
      toast({ variant: "destructive", title: "Enter a valid phone number" });
      return;
    }
    if (pan && !PAN_REGEX.test(pan.trim())) {
      toast({ variant: "destructive", title: "PAN must be in the format ABCDE1234F" });
      return;
    }
    if (residency !== "yes") {
      toast({ variant: "destructive", title: "Please confirm your residency status" });
      return;
    }

    setLoading(true);
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) throw new Error("checkout-script-failed");

      const { fbp, fbc } = getFbCookies();
      const donorPayload = {
        donation_type: donationType,
        amount: effectiveAmount,
        donor_name: name.trim(),
        donor_email: email.trim(),
        donor_phone: phone.trim(),
        donor_pan: pan.trim() || undefined,
        is_indian_resident: residency === "yes",
        fbp: fbp || undefined,
        fbc: fbc || undefined,
        campaign: DIWALI_CAMPAIGN_TAG,
        ...getAttribPayload(),
        ...getSrcTrackingPayload(),
      };

      const contentName = donationType === "monthly" ? "Diwali Gift Monthly First" : "Diwali Gift One Time";
      const contentCategory = donationType === "monthly" ? "Guardian Monthly" : "Guardian One-Time";
      const contentIds = [donationType === "monthly" ? "guardian-monthly" : "guardian-one-time"];

      const commonOptions = {
        name: "Animal Matter To Me",
        image: AMTM_LOGO_URL,
        prefill: { name: name.trim(), email: email.trim(), contact: phone.trim() },
        theme: { color: "#FFD85F" },
        modal: {
          ondismiss: () => {
            dispatchCheckoutClose();
            setLoading(false);
            onCheckoutCancelled?.();
          },
        },
      };

      let rzp;
      if (donationType === "monthly") {
        const { data } = await api.post("/guardian/checkout/subscription", donorPayload);
        rzp = new window.Razorpay({
          ...commonOptions,
          key: data.key_id,
          subscription_id: data.subscription_id,
          description: `AMTM Diwali Guardian - ₹${effectiveAmount.toLocaleString("en-IN")}/month`,
          handler: (response) => {
            // Razorpay has already told the browser this payment succeeded -
            // the subscription.charged/activated webhook is the real source
            // of truth reconciling the record; /verify is best-effort only.
            trackPurchase({
              value: effectiveAmount,
              donationType: "monthly",
              transactionId: response.razorpay_payment_id,
              contentName,
              contentCategory,
              contentIds,
            });
            dispatchCheckoutClose();
            setLoading(false);
            setSuccessMessage("Thank you for your Diwali gift to our animals and caregivers.");
            api
              .post("/guardian/verify/subscription", {
                razorpay_subscription_id: response.razorpay_subscription_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              })
              .catch((err) => console.error("guardian verify/subscription failed (non-blocking, donor already sees success):", err));
          },
        });
      } else {
        const { data } = await api.post("/guardian/checkout/order", donorPayload);
        rzp = new window.Razorpay({
          ...commonOptions,
          key: data.key_id,
          amount: data.amount_paise,
          currency: "INR",
          order_id: data.order_id,
          description: "Diwali gift to AMTM",
          handler: (response) => {
            trackPurchase({
              value: effectiveAmount,
              donationType: "one_time",
              transactionId: response.razorpay_payment_id,
              contentName,
              contentCategory,
              contentIds,
            });
            dispatchCheckoutClose();
            setLoading(false);
            setSuccessMessage("Thank you for your Diwali gift to our animals and caregivers.");
            api
              .post("/guardian/verify/order", {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              })
              .catch((err) => console.error("guardian verify/order failed (non-blocking, donor already sees success):", err));
          },
        });
      }

      rzp.on("payment.failed", () => {
        toast({ variant: "destructive", title: "Payment failed", description: "Please try again." });
      });

      onBeforeCheckout?.();
      dispatchCheckoutOpen();
      trackInitiateCheckout({
        value: effectiveAmount,
        donationType: donationType === "monthly" ? "monthly" : "one_time",
        contentName,
        contentCategory,
        contentIds,
      });
      rzp.open();
    } catch {
      toast({ variant: "destructive", title: "Could not start checkout", description: "Please try again." });
      setLoading(false);
    }
  };

  if (successMessage) {
    return (
      <div className={`bg-white rounded-3xl border border-amtm-border p-8 text-center ${className}`} data-testid={tid("widget-success")}>
        <Heart className="h-10 w-10 text-amtm-giveText mx-auto mb-4" />
        <p className="font-display text-xl text-amtm-text mb-2" data-testid={tid("widget-success-message")}>{successMessage}</p>
        <button
          type="button"
          onClick={() => setSuccessMessage(null)}
          data-testid={tid("widget-give-again-button")}
          className="text-sm text-amtm-giveText underline mt-3"
        >
          Make another gift
        </button>

        {donationType === "one_time" && (
          <div className="mt-6 pt-6 border-t border-amtm-border" data-testid={tid("one-time-upsell")}>
            <p className="text-sm text-amtm-text mb-3">Make it last beyond Diwali. Become a Guardian from ₹100 a month.</p>
            <Link to="/guardians?utm_source=diwali&utm_campaign=diwali2026_upsell" data-testid={tid("upsell-guardian-link")}>
              <Button className="bg-amtm-teal text-white hover:bg-amtm-tealHover font-semibold">Become a Guardian</Button>
            </Link>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-3xl border border-amtm-border shadow-[0_8px_30px_rgba(0,0,0,0.08)] p-5 md:p-7 ${className}`} data-testid={tid("donation-widget")}>
      <div className="grid grid-cols-2 gap-1.5 bg-amtm-bg rounded-full p-1.5 mb-5">
        <button
          type="button"
          onClick={() => switchType("one_time")}
          data-testid={tid("widget-toggle-onetime")}
          className={`h-10 rounded-full text-sm font-semibold transition-colors ${
            donationType === "one_time" ? "bg-[#FFD85F] text-[#1F0E06] shadow-sm" : "text-amtm-textMuted"
          }`}
        >
          One-time
        </button>
        <button
          type="button"
          onClick={() => switchType("monthly")}
          data-testid={tid("widget-toggle-monthly")}
          className={`h-10 rounded-full text-sm font-semibold transition-colors ${
            donationType === "monthly" ? "bg-[#FFD85F] text-[#1F0E06] shadow-sm" : "text-amtm-textMuted"
          }`}
        >
          Monthly
        </button>
      </div>

      {donationType === "monthly" ? (
        <MonthlyTierPicker testIdPrefix={testIdPrefix} selectedAmount={monthlyAmount} hasCustomAmount={!!customAmount} onSelect={(a) => { setMonthlyAmount(a); setCustomAmount(""); }} />
      ) : (
        <DiwaliTierPicker testIdPrefix={testIdPrefix} selectedAmount={oneTimeAmount} hasCustomAmount={!!customAmount} onSelect={(a) => { setOneTimeAmount(a); setCustomAmount(""); }} />
      )}

      <div className="mt-3">
        <Input
          type="number"
          min={MIN_DONATION}
          value={customAmount}
          onChange={(e) => setCustomAmount(e.target.value)}
          placeholder="Other amount (₹)"
          data-testid={tid("custom-amount-input")}
          className={`h-11 ${customAmountBelowMin ? "border-destructive focus-visible:ring-destructive" : ""}`}
        />
        {customAmountBelowMin && (
          <p className="text-xs text-destructive mt-1.5" data-testid={tid("custom-amount-min-error")}>
            Minimum donation is ₹{MIN_DONATION}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div>
          <Label htmlFor={tid("name")}>Full Name</Label>
          <Input id={tid("name")} value={name} onChange={(e) => setName(e.target.value)} data-testid={tid("donor-name-input")} className="mt-1.5 h-11" />
        </div>
        <div>
          <Label htmlFor={tid("email")}>Email</Label>
          <Input id={tid("email")} type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid={tid("donor-email-input")} className="mt-1.5 h-11" />
        </div>
      </div>
      <div className="mt-3">
        <Label htmlFor={tid("phone")}>Phone</Label>
        <Input id={tid("phone")} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid={tid("donor-phone-input")} className="mt-1.5 h-11" />
      </div>

      <div className="grid grid-cols-2 gap-2 mt-4">
        <button
          type="button"
          onClick={() => setResidency("yes")}
          data-testid={tid("residency-yes")}
          className={`h-11 rounded-xl border text-xs sm:text-sm font-medium px-2 transition-colors ${
            residency === "yes" ? "border-amtm-teal bg-amtm-teal/[0.06] text-amtm-text" : "border-amtm-border text-amtm-textMuted"
          }`}
        >
          I am an Indian resident / citizen
        </button>
        <button
          type="button"
          onClick={() => setResidency("no")}
          data-testid={tid("residency-no")}
          className={`h-11 rounded-xl border text-xs sm:text-sm font-medium px-2 transition-colors ${
            residency === "no" ? "border-destructive bg-destructive/[0.06] text-destructive" : "border-amtm-border text-amtm-textMuted"
          }`}
        >
          I'm not an Indian resident
        </button>
      </div>

      {residency === "no" && (
        <p className="text-xs text-amtm-textMuted bg-amtm-bg border border-amtm-border rounded-xl px-3.5 py-3 mt-3" data-testid={tid("fcra-message")}>
          Foreign contributions must be received through AMTM's designated FCRA account. Please write to{" "}
          <a href="mailto:getinvolved@amtmindia.org" className="text-amtm-giveText underline">
            getinvolved@amtmindia.org
          </a>{" "}
          and our team will help you give from outside India.
        </p>
      )}

      <div className="mt-3">
        <Label htmlFor={tid("pan")}>PAN (optional — required for your 80G tax exemption certificate)</Label>
        <Input
          id={tid("pan")}
          value={pan}
          onChange={(e) => setPan(e.target.value.toUpperCase())}
          placeholder="ABCDE1234F"
          maxLength={10}
          data-testid={tid("pan-input")}
          className="mt-1.5 h-11 uppercase"
        />
      </div>

      <Button
        onClick={handleDonate}
        disabled={loading || residency === "no" || customAmountBelowMin}
        className="w-full h-12 bg-[#FFD85F] text-[#1F0E06] hover:bg-[#f0c94a] font-semibold mt-5"
        data-testid={tid("submit-button")}
      >
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : donationType === "monthly" ? (
          <>
            <Heart className="h-5 w-5 mr-2" /> Give ₹{effectiveAmount.toLocaleString("en-IN")}/month
          </>
        ) : (
          <>
            <Heart className="h-5 w-5 mr-2" /> Send my Diwali gift
          </>
        )}
      </Button>

      <p className="text-xs text-amtm-textMuted text-center mt-3 flex items-center justify-center gap-1.5" data-testid={tid("trust-line")}>
        <ShieldCheck className="h-3.5 w-3.5 shrink-0" /> Secure payment via Razorpay &middot; 80G tax exemption &middot; You can cancel anytime.
      </p>
    </div>
  );
}
